import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Separator } from "../components/ui/separator";
import { AspectRatio } from "../components/ui/aspect-ratio";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../components/ui/command";
import { Menubar, MenubarMenu, MenubarTrigger, MenubarContent, MenubarItem } from "../components/ui/menubar";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator } from "../components/ui/breadcrumb";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../components/ui/collapsible";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "../components/ui/resizable";
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from "../components/ui/input-otp";
import { Switch } from "../components/ui/switch";
import { MapPin, Star, Thermometer, Waves, Mail, User, Lock, Building2, Phone, Globe, Instagram, ChevronDown, Menu, Search } from "lucide-react";
import Logo from "../components/Logo";
import AllAuthFormsDemo from "./AllAuthFormsDemo";
import { OperatorListingWizard } from "../components/onboarding/OperatorListingWizard";
import Contact from "./Contact";
import EmailTemplatesShowcase from "../components/design-system/EmailTemplatesShowcase";
import { Link } from "react-router-dom";

const DesignSystem = () => {
  return (
    <div className="min-h-screen bg-sauna-beige py-12 px-4 sm:px-6 md:px-8 lg:px-12">
      <Helmet>
        <title>Design System | SaunaDip</title>
      </Helmet>
      <div className="max-w-7xl mx-auto">
        <header className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-sauna-navy mb-4">SaunaDip Design System</h1>
          <div className="h-1 w-24 bg-gradient-to-r from-sauna-orange to-sauna-blue rounded-full mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl">
            A comprehensive guide to our design system, including brand guidelines, components, and usage patterns.
          </p>
        </header>

        <Tabs defaultValue="brand" className="w-full">
          <ScrollArea className="w-full">
            <TabsList className="mb-6 flex flex-wrap">
              <TabsTrigger value="brand">Brand</TabsTrigger>
              <TabsTrigger value="components">Components</TabsTrigger>
              <TabsTrigger value="forms">Forms</TabsTrigger>
              <TabsTrigger value="patterns">Patterns</TabsTrigger>
              <TabsTrigger value="emails">Emails</TabsTrigger>
              <TabsTrigger value="dashboards">Dashboards</TabsTrigger>
              <TabsTrigger value="tech">Tech Stack</TabsTrigger>
            </TabsList>
          </ScrollArea>

          {/* Brand Tab */}
          <TabsContent value="brand">
            <div className="space-y-12">
              {/* Brand Overview */}
              <section>
                <h2 className="text-2xl font-semibold text-sauna-navy mb-4">Brand Overview</h2>
                <p className="text-base text-gray-700 mb-6">
                  SaunaDip is Ireland & UK's first comprehensive directory for hot and cold wellness experiences. 
                  Our brand is modern, clean, and wellness-focused.
                </p>
              </section>

              {/* Logo */}
              <section>
                <h2 className="text-2xl font-semibold text-sauna-navy mb-4">Logo</h2>
                <div className="flex items-center gap-8 bg-white rounded-lg p-6 shadow-sm mb-2">
                  <Logo size="md" />
                  <span className="text-gray-600">
                    Use the logo on a white or sauna-beige background. Do not stretch, recolor, or place on busy backgrounds.
                  </span>
                </div>
                <pre className="bg-gray-100 rounded p-3 text-xs mt-2">
                  {'import Logo from "../components/Logo";\n<Logo size="md" />'}
                </pre>
              </section>

              {/* Color Palette */}
              <section>
                <h2 className="text-2xl font-semibold text-sauna-navy mb-4">Color Palette</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 mb-4">
                  <div>
                    <div className="h-16 bg-sauna-orange rounded-lg shadow-sm mb-2"></div>
                    <p className="font-medium">Orange</p>
                    <p className="text-sm text-gray-500">#F5542D</p>
                    <p className="text-xs text-gray-500">CTAs, highlights</p>
                  </div>
                  <div>
                    <div className="h-16 bg-sauna-blue rounded-lg shadow-sm mb-2"></div>
                    <p className="font-medium">Blue</p>
                    <p className="text-sm text-gray-500">#0884E4</p>
                    <p className="text-xs text-gray-500">Cold therapy elements</p>
                  </div>
                  <div>
                    <div className="h-16 bg-sauna-navy rounded-lg shadow-sm mb-2"></div>
                    <p className="font-medium">Navy</p>
                    <p className="text-sm text-gray-500">#0C3535</p>
                    <p className="text-xs text-gray-500">Headings, important text</p>
                  </div>
                  <div>
                    <div className="h-16 bg-sauna-beige rounded-lg shadow-sm border mb-2"></div>
                    <p className="font-medium">Beige</p>
                    <p className="text-sm text-gray-500">#F5F5F3</p>
                    <p className="text-xs text-gray-500">Backgrounds, subtlety</p>
                  </div>
                  <div>
                    <div className="h-16 bg-sauna-green rounded-lg shadow-sm mb-2"></div>
                    <p className="font-medium">Green</p>
                    <p className="text-sm text-gray-500">#2ECC71</p>
                    <p className="text-xs text-gray-500">Success states</p>
                  </div>
                  <div>
                    <div className="h-16 bg-sauna-red rounded-lg shadow-sm mb-2"></div>
                    <p className="font-medium">Red</p>
                    <p className="text-sm text-gray-500">#E74C3C</p>
                    <p className="text-xs text-gray-500">Error states</p>
                  </div>
                </div>
              </section>

              {/* Typography */}
              <section>
                <h2 className="text-2xl font-semibold text-sauna-navy mb-4">Typography</h2>
                <div className="space-y-4 bg-white rounded-lg p-6 shadow-sm mb-2">
                  <h1 className="text-5xl font-bold text-sauna-navy">Heading 1</h1>
                  <h2 className="text-4xl font-bold text-sauna-navy">Heading 2</h2>
                  <h3 className="text-3xl font-semibold text-sauna-navy">Heading 3</h3>
                  <h4 className="text-2xl font-semibold text-sauna-navy">Heading 4</h4>
                  <h5 className="text-xl font-medium text-sauna-navy">Heading 5</h5>
                  <h6 className="text-lg font-medium text-sauna-navy">Heading 6</h6>
                  <p className="text-base text-gray-800 mt-4">
                    Base paragraph text. SaunaDip combines the invigorating rush of cold water immersion with the relaxing warmth of sauna sessions.
                  </p>
                  <p className="text-lg text-gray-800">
                    Large paragraph text. Experience the health benefits of contrast therapy through our guided sauna and cold plunge sessions.
                  </p>
                  <p className="text-sm text-gray-600">
                    Small text for less important information or supporting context.
                  </p>
                  <p className="text-xs text-gray-500">
                    Extra small text for captions, labels, and metadata.
                  </p>
                </div>
                <pre className="bg-gray-100 rounded p-3 text-xs mt-2">
                  {'text-sauna-navy font-bold text-3xl, etc.'}
                </pre>
              </section>

              {/* Spacing */}
              <section>
                <h2 className="text-2xl font-semibold text-sauna-navy mb-4">Spacing</h2>
                <div className="bg-white rounded-lg p-6 shadow-sm mb-2">
                  <div className="flex flex-wrap gap-4 items-center">
                    <div className="w-12 h-4 bg-sauna-orange rounded"></div>
                    <span className="text-sm text-gray-600">
                      Spacing scale: 4px, 8px, 12px, 16px, 24px, 32px, 40px, 48px, 64px
                    </span>
                  </div>
                </div>
                <pre className="bg-gray-100 rounded p-3 text-xs mt-2">
                  {'p-4, px-6, gap-8, etc. (see tailwind.config.ts for all spacing tokens)'}
                </pre>
              </section>
            </div>
          </TabsContent>

          {/* Components Tab */}
          <TabsContent value="components">
            <div className="space-y-12">
              {/* Basic UI Components */}
              <section>
                <h2 className="text-2xl font-semibold text-sauna-navy mb-4">Basic UI Components</h2>
                
                {/* Buttons */}
                <div className="mb-8">
                  <h3 className="text-xl font-medium text-sauna-navy mb-4">Buttons</h3>
                  <div className="flex flex-wrap gap-4 bg-white rounded-lg p-6 shadow-sm mb-2">
                    <Button>Default</Button>
                    <Button variant="outline">Outline</Button>
                    <Button variant="secondary">Secondary</Button>
                    <Button className="bg-sauna-orange text-white">Sauna Orange</Button>
                    <Button disabled>Disabled</Button>
                  </div>
                  <pre className="bg-gray-100 rounded p-3 text-xs mt-2">
                    {'import { Button } from "../components/ui/button";\n<Button>Default</Button>'}
                  </pre>
                </div>

                {/* Form Elements */}
                <div className="mb-8">
                  <h3 className="text-xl font-medium text-sauna-navy mb-4">Form Elements</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white rounded-lg p-6 shadow-sm mb-2">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="input">Input</Label>
                        <Input id="input" placeholder="Type here..." />
                      </div>
                      <div>
                        <Label htmlFor="textarea">Textarea</Label>
                        <Textarea id="textarea" placeholder="Type more..." />
                      </div>
                      <div>
                        <Label htmlFor="select">Select</Label>
                        <Select>
                          <SelectTrigger id="select">
                            <SelectValue placeholder="Select an option" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="one">One</SelectItem>
                            <SelectItem value="two">Two</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="checkbox">Checkbox</Label>
                        <Checkbox id="checkbox" />
                      </div>
                      <div>
                        <Label htmlFor="radio">Radio Group</Label>
                        <RadioGroup defaultValue="1">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="1" id="r1" />
                            <Label htmlFor="r1">Option 1</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="2" id="r2" />
                            <Label htmlFor="r2">Option 2</Label>
                          </div>
                        </RadioGroup>
                      </div>
                      <div>
                        <Label htmlFor="switch">Switch</Label>
                        <Switch id="switch" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cards */}
                <div className="mb-8">
                  <h3 className="text-xl font-medium text-sauna-navy mb-4">Cards</h3>
                  <div className="flex flex-wrap gap-4 bg-white rounded-lg p-6 shadow-sm mb-2">
                    <Card className="w-64">
                      <CardHeader>
                        <CardTitle>Card Title</CardTitle>
                        <CardDescription>Card description goes here.</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p>Card content area.</p>
                      </CardContent>
                      <CardFooter>
                        <Button size="sm">Action</Button>
                      </CardFooter>
                    </Card>
                  </div>
                </div>

                {/* Badges */}
                <div className="mb-8">
                  <h3 className="text-xl font-medium text-sauna-navy mb-4">Badges</h3>
                  <div className="flex flex-wrap gap-4 bg-white rounded-lg p-6 shadow-sm mb-2">
                    <Badge>Default</Badge>
                    <Badge variant="secondary">Secondary</Badge>
                    <Badge className="bg-sauna-orange text-white">Sauna Orange</Badge>
                  </div>
                </div>

                {/* Complex Components */}
                <div className="mb-8">
                  <h3 className="text-xl font-medium text-sauna-navy mb-4">Complex Components</h3>
                  
                  {/* Dialog */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-sauna-navy mb-3">Dialog</h4>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button>Open Dialog</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Dialog Title</DialogTitle>
                          <DialogDescription>
                            This is a dialog component example.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                          <p>Dialog content goes here.</p>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {/* Dropdown Menu */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-sauna-navy mb-3">Dropdown Menu</h4>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline">Open Menu</Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem>Item 1</DropdownMenuItem>
                        <DropdownMenuItem>Item 2</DropdownMenuItem>
                        <DropdownMenuItem>Item 3</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Command Menu */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-sauna-navy mb-3">Command Menu</h4>
                    <Command className="rounded-lg border shadow-md">
                      <CommandInput placeholder="Type a command or search..." />
                      <CommandList>
                        <CommandEmpty>No results found.</CommandEmpty>
                        <CommandGroup heading="Suggestions">
                          <CommandItem>Calendar</CommandItem>
                          <CommandItem>Search</CommandItem>
                          <CommandItem>Settings</CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </div>

                  {/* Menubar */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-sauna-navy mb-3">Menubar</h4>
                    <Menubar>
                      <MenubarMenu>
                        <MenubarTrigger>File</MenubarTrigger>
                        <MenubarContent>
                          <MenubarItem>New</MenubarItem>
                          <MenubarItem>Open</MenubarItem>
                          <MenubarItem>Save</MenubarItem>
                        </MenubarContent>
                      </MenubarMenu>
                    </Menubar>
                  </div>

                  {/* Breadcrumbs */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-sauna-navy mb-3">Breadcrumbs</h4>
                    <Breadcrumb>
                      <BreadcrumbList>
                        <BreadcrumbItem>
                          <BreadcrumbLink href="/">Home</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                          <BreadcrumbLink href="/components">Components</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>Breadcrumb</BreadcrumbItem>
                      </BreadcrumbList>
                    </Breadcrumb>
                  </div>

                  {/* Collapsible */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-sauna-navy mb-3">Collapsible</h4>
                    <Collapsible>
                      <CollapsibleTrigger asChild>
                        <Button variant="outline">Toggle Content</Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="p-4 bg-white rounded-lg mt-2">
                        <p>This is collapsible content.</p>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>

                  {/* Resizable Panels */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-sauna-navy mb-3">Resizable Panels</h4>
                    <ResizablePanelGroup direction="horizontal" className="min-h-[200px] border rounded-lg">
                      <ResizablePanel defaultSize={25}>
                        <div className="p-4">Panel 1</div>
                      </ResizablePanel>
                      <ResizableHandle />
                      <ResizablePanel defaultSize={75}>
                        <div className="p-4">Panel 2</div>
                      </ResizablePanel>
                    </ResizablePanelGroup>
                  </div>

                  {/* Aspect Ratio */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-sauna-navy mb-3">Aspect Ratio</h4>
                    <AspectRatio ratio={16 / 9} className="bg-sauna-beige rounded-lg overflow-hidden">
                      <div className="w-full h-full flex items-center justify-center text-sauna-navy">
                        Aspect Ratio 16:9
                      </div>
                    </AspectRatio>
                  </div>
                </div>
              </section>
            </div>
          </TabsContent>

          {/* Forms Tab */}
          <TabsContent value="forms">
            <div className="space-y-12">
              <section>
                <h2 className="text-2xl font-semibold text-sauna-navy mb-4">Authentication Forms</h2>
                <p className="text-base text-gray-700 mb-6">
                  All authentication forms (login, signup, forgot password) in their latest design.
                </p>
                <AllAuthFormsDemo />
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-sauna-navy mb-4">Operator Listing Wizard</h2>
                <p className="text-base text-gray-700 mb-6">
                  Multi-step onboarding wizard for operators.
                </p>
                <OperatorListingWizard />
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-sauna-navy mb-4">Contact Form</h2>
                <p className="text-base text-gray-700 mb-6">
                  Contact form for general inquiries.
                </p>
                <div className="max-w-3xl mx-auto">
                  <Contact />
                </div>
              </section>
            </div>
          </TabsContent>

          {/* Patterns Tab */}
          <TabsContent value="patterns">
            <div className="space-y-12">
              {/* Directory Components */}
              <section>
                <h2 className="text-2xl font-semibold text-sauna-navy mb-4">Directory Components</h2>
                <div className="space-y-8">
                  {/* Directory Filters */}
                  <Card className="w-full max-w-2xl mx-auto">
                    <CardHeader>
                      <CardTitle>Directory Filters</CardTitle>
                      <CardDescription>Filter and search venues</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex gap-4">
                          <Input placeholder="Search venues..." className="flex-1" />
                          <Select>
                            <SelectTrigger className="w-[180px]">
                              <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Types</SelectItem>
                              <SelectItem value="sauna">Sauna</SelectItem>
                              <SelectItem value="plunge">Cold Plunge</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="cursor-pointer">Towels Provided</Badge>
                          <Badge variant="outline" className="cursor-pointer">Showers</Badge>
                          <Badge variant="outline" className="cursor-pointer">Parking</Badge>
                          <Badge variant="outline" className="cursor-pointer">Changing Rooms</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Venue Card */}
                  <Card className="w-full max-w-2xl mx-auto">
                    <CardHeader>
                      <CardTitle>Venue Card</CardTitle>
                      <CardDescription>Example venue listing card</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="border rounded-lg overflow-hidden">
                        <AspectRatio ratio={16 / 9}>
                          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                            <span className="text-gray-500">Venue Image</span>
                          </div>
                        </AspectRatio>
                        <div className="p-4">
                          <h3 className="text-lg font-semibold">Sauna Paradise</h3>
                          <p className="text-sm text-gray-600 mb-2">Traditional Finnish Sauna Experience</p>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              <span>Dublin, Ireland</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-yellow-400" />
                              <span>4.8 (120 reviews)</span>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 mb-3">
                            <Badge>Towels Provided</Badge>
                            <Badge>Showers</Badge>
                            <Badge>Parking</Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-lg font-semibold">€25</div>
                            <Button>View Details</Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </section>

              {/* Help Book Components */}
              <section>
                <h2 className="text-2xl font-semibold text-sauna-navy mb-4">Help Book Components</h2>
                <div className="space-y-8">
                  <Card className="w-full max-w-2xl mx-auto">
                    <CardHeader>
                      <CardTitle>Help Book Section</CardTitle>
                      <CardDescription>Example help book content</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        <div className="flex items-center gap-4 p-4 bg-sauna-beige/50 rounded-xl">
                          <div className="w-12 h-12 bg-sauna-navy rounded-lg flex items-center justify-center">
                            <Thermometer className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-medium text-sauna-navy">SaunaDip Wall Unit</h3>
                            <p className="text-sm text-sauna-navy/70">OLED display with Bluetooth receiver</p>
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-sauna-blue/10 to-sauna-orange/10 rounded-2xl p-6">
                          <h3 className="font-medium text-sauna-navy mb-4">Setup Guide</h3>
                          <div className="space-y-4">
                            <div className="flex gap-4">
                              <Badge className="bg-sauna-orange text-white font-bold min-w-8 h-8 flex items-center justify-center">1</Badge>
                              <div>
                                <h4 className="font-medium text-sauna-navy">Power On</h4>
                                <p className="text-sauna-navy/70">Press and hold the power button until the OLED display lights up.</p>
                              </div>
                            </div>
                            <div className="flex gap-4">
                              <Badge className="bg-sauna-orange text-white font-bold min-w-8 h-8 flex items-center justify-center">2</Badge>
                              <div>
                                <h4 className="font-medium text-sauna-navy">Connect to Setup Network</h4>
                                <p className="text-sauna-navy/70">On your phone, connect to the "SaunaDip_Setup" Wi-Fi network.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </section>
            </div>
          </TabsContent>

          {/* Emails Tab */}
          <TabsContent value="emails">
            <div className="space-y-12">
              <section>
                <h2 className="text-2xl font-semibold text-sauna-navy mb-4">Email Templates</h2>
                <p className="text-base text-gray-700 mb-6">
                  A collection of all email templates used throughout the application, including transactional emails, newsletters, and marketing communications.
                </p>
                <EmailTemplatesShowcase />
              </section>
            </div>
          </TabsContent>

          {/* Dashboards Tab */}
          <TabsContent value="dashboards">
            <section>
              <h2 className="text-2xl font-semibold text-sauna-navy mb-4">Dashboard Pages</h2>
              <p className="text-base text-gray-700 mb-6">
                Here are links to the various user-facing dashboards in the application.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>User Dashboard</CardTitle>
                    <CardDescription>Standard dashboard for logged-in users.</CardDescription>
                  </CardHeader>
                  <CardFooter>
                    <Button asChild>
                      <Link to="/user-dashboard" target="_blank">View Dashboard</Link>
                    </Button>
                  </CardFooter>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Guest Dashboard</CardTitle>
                    <CardDescription>Dashboard for users who track their dips.</CardDescription>
                  </CardHeader>
                  <CardFooter>
                    <Button asChild>
                      <Link to="/guest-dashboard" target="_blank">View Dashboard</Link>
                    </Button>
                  </CardFooter>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Operator Dashboard</CardTitle>
                    <CardDescription>Dashboard for business operators.</CardDescription>
                  </CardHeader>
                  <CardFooter>
                    <Button asChild>
                      <Link to="/operator-admin-simple" target="_blank">View Dashboard</Link>
                    </Button>
                  </CardFooter>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Admin Dashboard</CardTitle>
                    <CardDescription>Main dashboard for site administrators.</CardDescription>
                  </CardHeader>
                  <CardFooter>
                    <Button asChild>
                      <Link to="/admin/dashboards" target="_blank">View Dashboard</Link>
                    </Button>
                  </CardFooter>
                </Card>
                 <Card>
                  <CardHeader>
                    <CardTitle>Metrics Dashboard</CardTitle>
                    <CardDescription>Dashboard for viewing application metrics.</CardDescription>
                  </CardHeader>
                  <CardFooter>
                    <Button asChild>
                      <Link to="/admin/metrics" target="_blank">View Dashboard</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>
            </section>
          </TabsContent>

          {/* Tech Stack Tab */}
          <TabsContent value="tech">
            <div className="space-y-12">
              <section>
                <h2 className="text-2xl font-semibold text-sauna-navy mb-4">Technology Stack</h2>
                <div className="grid gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Frontend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="list-disc pl-6 space-y-2">
                        <li>React 18 with TypeScript</li>
                        <li>Next.js 14 for server-side rendering</li>
                        <li>Tailwind CSS for styling</li>
                        <li>Radix UI for accessible components</li>
                        <li>React Hook Form for form handling</li>
                        <li>Zod for validation</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Backend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="list-disc pl-6 space-y-2">
                        <li>PHP 8.2+ with Laravel 10.x</li>
                        <li>MySQL 8.0+ or PostgreSQL 14+</li>
                        <li>Redis for caching</li>
                        <li>Laravel Sanctum for authentication</li>
                        <li>Stripe for payments</li>
                        <li>Mailgun for transactional emails</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Development Tools</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="list-disc pl-6 space-y-2">
                        <li>GitHub for version control</li>
                        <li>GitHub Actions for CI/CD</li>
                        <li>Doppler for secrets management</li>
                        <li>New Relic for monitoring</li>
                        <li>Laravel Cloud for hosting</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </section>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DesignSystem;
